// The development finance comparison engine: the scheme's figures, the three
// refine sections, live results and the adviser band. Ported from the
// developmenteye.co.uk homepage script; the application dialog is the shared
// one (apply.ts).
import { setupApply, type ApplyAdapter, type ApplyState } from '../apply';
import { Parts, scrollToEl } from '../dom';
import { applyMoneyInputFormatting, classifyUkPostcode, formatMoneyInput, labelFor, money } from '../format';
import type { DevFinanceForm, DevResultRow, EngineConfig, MountOptions } from '../types';
import { devLowestTotalCostGbp, devResultRowHtml, devResultsHeaderHtml, sortDevResults, type DevSortBy } from './results';

const CHECK_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>';
const BADGE_CHECK_SVG = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>';
const RESULTS_PREVIEW_COUNT = 5;

interface DevState extends ApplyState {
  siteValue: number; existingDebt: number; buildCost: number; grossDevValue: number; cashEquity: number;
  projectType: string; planningStatus: string; region: string; regionGroup: string; postcode: string;
  buildDurationMonths: number; exitDurationMonths: number; completedUnits3yr: number;
  borrowerEntity: string; hasAdverseCredit: string;
  results: DevResultRow[]; apiFetchToken: number; sortBy: DevSortBy;
}

type MoneyKey = 'siteValue' | 'existingDebt' | 'buildCost' | 'grossDevValue' | 'cashEquity';
type ChoiceKey = 'projectType' | 'planningStatus' | 'borrowerEntity' | 'hasAdverseCredit';
type NumberKey = 'buildDurationMonths' | 'exitDurationMonths' | 'completedUnits3yr';

export function startDevelopmentEngine(parts: Parts, config: EngineConfig<DevFinanceForm>, options: MountOptions): void {
  const { form, brand } = config;
  const d = form.defaults;
  const $ = <T extends HTMLElement = HTMLElement>(id: string) => parts.el<T>(id);
  const scrollTo = (el: Element, block: 'start' | 'nearest' | 'center' = 'start') => scrollToEl(el, options.stickyTop, block);

  const state: DevState = {
    siteValue: d.siteValue, existingDebt: d.existingDebt, buildCost: d.buildCost, grossDevValue: d.grossDevValue, cashEquity: d.cashEquity,
    projectType: d.projectType, planningStatus: d.planningStatus, region: 'england_wales', regionGroup: d.regionGroup, postcode: '',
    buildDurationMonths: d.buildDurationMonths, exitDurationMonths: d.exitDurationMonths, completedUnits3yr: d.completedUnits3yr,
    borrowerEntity: d.borrowerEntity, hasAdverseCredit: d.hasAdverseCredit,
    quoteId: null, results: [], apiFetchToken: 0, sortBy: 'totalCost', selectedCode: null,
    who: 'borrower', introMode: null, introCompanyAddress: '', address: '', comments: '',
  };

  // Preset answers (a landing page or data-preset): only known keys with valid values.
  const choices: Record<ChoiceKey, [string, string][]> = {
    projectType: form.projectTypes, planningStatus: form.planningStatuses, borrowerEntity: form.borrowerEntities, hasAdverseCredit: form.adverse,
  };
  const preset = options.preset;
  if (preset) {
    (Object.keys(choices) as ChoiceKey[]).forEach((key) => {
      if (choices[key].some((o) => o[0] === preset[key])) state[key] = preset[key] as string;
    });
    (['siteValue', 'existingDebt', 'buildCost', 'grossDevValue', 'cashEquity'] as MoneyKey[]).forEach((key) => {
      const v = preset[key];
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) state[key] = v;
    });
  }

  // ---------- The scheme's figures ----------
  const facilityGbp = () => Math.max(0, state.siteValue + state.buildCost - state.cashEquity);
  const ltcPct = () => { const cost = state.siteValue + state.buildCost; return cost > 0 ? facilityGbp() / cost * 100 : 0; };
  const ltgdvPct = () => (state.grossDevValue > 0 ? facilityGbp() / state.grossDevValue * 100 : 0);

  // ---------- Refine: three sections ----------
  const SECTIONS = [
    { id: 'project', keys: ['projectType', 'planningStatus'] },
    { id: 'timing', keys: ['region', 'buildDurationMonths', 'exitDurationMonths'] },
    { id: 'experience', keys: ['completedUnits3yr', 'borrowerEntity', 'hasAdverseCredit'] },
  ];
  const touchedKeys = new Set<string>();
  const continuedSections = new Set<string>();

  function buildPills(container: HTMLElement, opts: [string, string][], key: ChoiceKey) {
    container.innerHTML = opts.map(([value, label]) =>
      `<button type="button" class="pill" data-value="${value}" aria-pressed="${state[key] === value}">${CHECK_SVG}${label}</button>`).join('');
    container.addEventListener('click', (e) => {
      const btn = (e.target as Element).closest<HTMLButtonElement>('button[data-value]');
      if (!btn) return;
      state[key] = btn.dataset.value as string;
      container.querySelectorAll('button[data-value]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
      touchedKeys.add(key);
      scheduleFetchResults();
      updateSections();
    });
  }
  buildPills($('input-project-type'), form.projectTypes, 'projectType');
  buildPills($('input-planning-status'), form.planningStatuses, 'planningStatus');
  buildPills($('input-borrower-entity'), form.borrowerEntities, 'borrowerEntity');
  buildPills($('input-adverse'), form.adverse, 'hasAdverseCredit');

  function sectionSummary(id: string): string {
    if (id === 'project') return [labelFor(form.projectTypes, state.projectType), labelFor(form.planningStatuses, state.planningStatus).toLowerCase()].join(' · ');
    if (id === 'timing') return [labelFor(form.regions, state.region), state.buildDurationMonths + 'mo build', state.exitDurationMonths + 'mo exit'].join(' · ');
    return [labelFor(form.borrowerEntities, state.borrowerEntity), state.completedUnits3yr + ' units completed', state.hasAdverseCredit === 'yes' ? 'adverse credit' : 'clean credit'].join(' · ');
  }

  function updateSections() {
    let currentAssigned = false;
    SECTIONS.forEach((s, i) => {
      const complete = continuedSections.has(s.id) || s.keys.every((k) => touchedKeys.has(k));
      const sectionEl = parts.all(`[data-section="${s.id}"]`)[0];
      sectionEl.classList.toggle('is-complete', complete);
      sectionEl.classList.toggle('is-collapsed', continuedSections.has(s.id));
      parts.all(`[data-badge="${s.id}"]`)[0].innerHTML = complete ? BADGE_CHECK_SVG : String(i + 1);
      parts.all(`[data-summary="${s.id}"]`)[0].textContent = sectionSummary(s.id);
      const progressEl = parts.all(`[data-progress="${s.id}"]`)[0];
      const isCurrent = !complete && !currentAssigned;
      if (isCurrent) currentAssigned = true;
      progressEl.classList.toggle('is-done', complete);
      progressEl.classList.toggle('is-current', isCurrent);
      const btn = progressEl.querySelector('button') as HTMLButtonElement;
      if (isCurrent) btn.setAttribute('aria-current', 'step'); else btn.removeAttribute('aria-current');
      (progressEl.querySelector('.num') as HTMLElement).innerHTML = complete ? BADGE_CHECK_SVG : String(i + 1);
    });
    const compareEl = parts.all('[data-progress="compare"]')[0];
    compareEl.classList.toggle('is-current', !currentAssigned);
    const compareBtn = compareEl.querySelector('button') as HTMLButtonElement;
    if (!currentAssigned) compareBtn.setAttribute('aria-current', 'step'); else compareBtn.removeAttribute('aria-current');
  }

  parts.all<HTMLButtonElement>('[data-continue]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.continue as string;
      continuedSections.add(id);
      updateSections();
      const idx = SECTIONS.findIndex((s) => s.id === id);
      const next = SECTIONS.slice(idx + 1).find((s) => !continuedSections.has(s.id));
      scrollTo(next ? $('sec-' + next.id) : $('results'));
    });
  });
  parts.all<HTMLButtonElement>('[data-change]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.change as string;
      continuedSections.delete(id);
      updateSections();
      scrollTo($('sec-' + id));
    });
  });
  parts.all<HTMLButtonElement>('[data-scroll]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.scroll as string;
      const el = target === 'estimate' ? parts.all('.estimate-card')[0] : parts.maybe(target);
      if (el) scrollTo(el);
    });
  });

  // Location: United Kingdom is narrowed to a region by the postcode.
  function syncRegionFromGroup() {
    $('postcode-field').classList.toggle('hidden', state.regionGroup !== 'uk');
    state.region = state.regionGroup === 'uk' ? classifyUkPostcode(form.postcodeAreaRegions, state.postcode) : state.regionGroup;
  }
  const regionSelect = $<HTMLSelectElement>('input-region-group');
  regionSelect.value = state.regionGroup;
  syncRegionFromGroup();
  regionSelect.addEventListener('change', () => {
    state.regionGroup = regionSelect.value;
    touchedKeys.add('region');
    syncRegionFromGroup();
    scheduleFetchResults();
    updateSections();
  });
  $<HTMLInputElement>('input-postcode').addEventListener('input', (e) => {
    state.postcode = (e.target as HTMLInputElement).value;
    touchedKeys.add('region');
    syncRegionFromGroup();
    scheduleFetchResults();
    updateSections();
  });

  ([['input-build-duration', 'buildDurationMonths', 1], ['input-exit-duration', 'exitDurationMonths', 0], ['input-completed-units', 'completedUnits3yr', 0]] as [string, NumberKey, number][]).forEach(([id, key, min]) => {
    const el = $<HTMLInputElement>(id);
    el.value = String(state[key]);
    el.addEventListener('input', () => {
      state[key] = Math.max(min, Math.round(Number(el.value) || 0));
      touchedKeys.add(key);
      scheduleFetchResults();
      updateSections();
    });
  });

  // ---------- The quick estimate's money fields ----------
  // The example figures clear on first click; leaving without typing puts them back.
  const moneyFieldsEdited = new Set<string>();
  ([['input-site-value', 'siteValue'], ['input-existing-debt', 'existingDebt'], ['input-build-cost', 'buildCost'], ['input-gdv', 'grossDevValue'], ['input-cash-equity', 'cashEquity']] as [string, MoneyKey][]).forEach(([id, key]) => {
    const el = $<HTMLInputElement>(id);
    el.value = formatMoneyInput(state[key]);
    el.addEventListener('focus', () => { if (!moneyFieldsEdited.has(id)) el.value = ''; });
    el.addEventListener('blur', () => { if (!moneyFieldsEdited.has(id) && el.value === '') el.value = formatMoneyInput(state[key]); });
    el.addEventListener('input', () => {
      moneyFieldsEdited.add(id);
      state[key] = applyMoneyInputFormatting(el);
      scheduleFetchResults();
    });
  });

  function render() {
    const hasFigures = facilityGbp() > 0 && state.grossDevValue > 0;
    $('facility-label').textContent = money(facilityGbp());
    $('ltc-label').textContent = hasFigures ? ltcPct().toFixed(1) + '%' : '—';
    $('ltgdv-label').textContent = hasFigures ? ltgdvPct().toFixed(1) + '%' : '—';
    const values: Record<string, string> = {
      site: money(state.siteValue), build: money(state.buildCost), gdv: money(state.grossDevValue),
      facility: money(facilityGbp()), ltgdv: hasFigures ? ltgdvPct().toFixed(1) + '%' : '—',
    };
    parts.all('[data-case]').forEach((el) => { el.textContent = values[el.dataset.case as string]; });
  }

  // ---------- Results ----------
  let showAllResults = false;
  function renderResultRows() {
    $('results-header-grid').innerHTML = devResultsHeaderHtml();
    const sorted = sortDevResults(state.results, state.sortBy);
    const visible = showAllResults ? sorted : sorted.slice(0, RESULTS_PREVIEW_COUNT);
    const lowestCost = devLowestTotalCostGbp(state.results);
    const rowsEl = $('results-rows');
    rowsEl.innerHTML = visible.map((r, i) => devResultRowHtml(r, i, lowestCost !== null && Math.round(r.totalCostGbp) === lowestCost)).join('');
    $('results-more-wrap').classList.toggle('hidden', sorted.length <= RESULTS_PREVIEW_COUNT);
    $('results-show-all-btn').textContent = showAllResults ? `Show top ${RESULTS_PREVIEW_COUNT} only` : `Show all ${sorted.length} products`;
    rowsEl.querySelectorAll<HTMLButtonElement>('[data-toggle-details]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const wrap = $(btn.getAttribute('aria-controls') as string);
        const open = wrap.classList.toggle('open');
        btn.textContent = open ? 'Details ▴' : 'Details ▾';
        btn.setAttribute('aria-expanded', String(open));
      });
    });
    rowsEl.querySelectorAll<HTMLButtonElement>('[data-apply]').forEach((btn) => {
      btn.addEventListener('click', () => { state.selectedCode = btn.dataset.apply as string; apply.openApplyDialog(); });
    });
  }
  $('results-show-all-btn').addEventListener('click', () => { showAllResults = !showAllResults; renderResultRows(); });
  $<HTMLSelectElement>('input-results-sort').addEventListener('change', (e) => { state.sortBy = (e.target as HTMLSelectElement).value as DevSortBy; renderResultRows(); });

  const show = (id: string, visible: boolean) => $(id).classList.toggle('hidden', !visible);
  function setRatePanelMessage(text: string) {
    $('rate-panel-headline').textContent = text;
    show('rate-panel-headline', true); show('rate-panel-figure-wrap', false); show('rate-panel-breakdown', false);
  }
  function showResultsState(which: 'results' | 'none' | 'not-ready') {
    show('results-wrap', which === 'results'); show('results-sort-wrap', which === 'results');
    show('no-results-wrap', which === 'none'); show('not-ready-wrap', which === 'not-ready');
  }

  let fetchDebounce: ReturnType<typeof setTimeout> | undefined;
  function scheduleFetchResults() {
    render();
    clearTimeout(fetchDebounce);
    fetchDebounce = setTimeout(() => { void fetchResults(); }, 500);
  }

  async function fetchResults(): Promise<void> {
    const token = ++state.apiFetchToken;
    $('results-loan-label').textContent = money(facilityGbp());
    if (!(facilityGbp() > 0 && state.grossDevValue > 0)) {
      $('results-headline').textContent = 'Complete the details above';
      $('results-criteria-line').textContent = '';
      showResultsState('not-ready');
      setRatePanelMessage('Enter your site, build cost and GDV to see available products');
      return;
    }
    $('results-headline').textContent = 'Checking available products…';
    setRatePanelMessage('Checking available products…');

    const body = {
      financeType: form.financeType,
      siteValueGbp: state.siteValue,
      existingDebtGbp: state.existingDebt,
      buildCostGbp: state.buildCost,
      grossDevValueGbp: state.grossDevValue,
      cashEquityGbp: state.cashEquity,
      projectType: state.projectType,
      planningStatus: state.planningStatus,
      region: state.region,
      buildDurationMonths: Number(state.buildDurationMonths),
      exitDurationMonths: Number(state.exitDurationMonths),
      completedUnits3yr: Number(state.completedUnits3yr),
      borrowerEntity: state.borrowerEntity,
      hasAdverseCredit: state.hasAdverseCredit === 'yes',
    };
    const endpoint = options.mode === 'admin' ? '/api/quote/stage1-admin-preview' : '/api/quote/stage1';

    let data: { quoteId: string; results: DevResultRow[]; errors?: { field: string; message: string }[] };
    try {
      const res = await fetch(endpoint, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
      data = await res.json();
      if (!res.ok) throw new Error((data.errors || []).map((e) => e.field + ': ' + e.message).join('; ') || 'Request failed');
    } catch (err) {
      if (token !== state.apiFetchToken) return;
      $('results-headline').textContent = 'Could not check available products';
      $('results-criteria-line').textContent = (err as Error).message;
      setRatePanelMessage('Could not check available products');
      return;
    }
    if (token !== state.apiFetchToken) return; // superseded by a newer request

    state.quoteId = data.quoteId;
    state.results = data.results;
    $('results-criteria-line').textContent = [sectionSummary('project'), sectionSummary('timing'), labelFor(form.borrowerEntities, state.borrowerEntity)].join(' · ');

    if (data.results.length === 0) {
      $('results-headline').textContent = 'Speak to an adviser';
      showResultsState('none');
      setRatePanelMessage('An adviser can source terms for this scheme');
      return;
    }
    $('results-headline').textContent = data.results.length + (data.results.length === 1 ? ' product matches your scheme' : ' products match your scheme');
    showResultsState('results');
    renderResultRows();

    const best = sortDevResults(data.results, 'totalCost')[0];
    show('rate-panel-headline', false); show('rate-panel-figure-wrap', true); show('rate-panel-breakdown', true);
    $('rate-panel-figure').textContent = best.rateMonthlyPct.toFixed(2) + '%';
    $('rate-panel-facility').textContent = money(best.facilityGbp);
    $('rate-panel-fee').textContent = money(best.arrangementFeeAmountGbp);
    $('rate-panel-total').textContent = money(best.totalCostGbp);
  }

  // What the shared application dialog needs to know about a scheme.
  const adapter: ApplyAdapter = {
    financeType: form.financeType,
    state,
    caseNoun: 'scheme',
    timescaleNote: 'Facility needed',
    selectedProduct(code) {
      const sel = state.results.find((r) => r.code === code);
      if (!sel) return null;
      return {
        lenderName: sel.lenderName || sel.code,
        summary: `${sel.productName ? sel.productName + ' · ' : ''}${money(sel.facilityGbp)} facility · ${sel.rateMonthlyPct.toFixed(2)}% pm · ${money(sel.totalCostGbp)} total cost`,
      };
    },
    showCompanyField: () => state.borrowerEntity !== 'individual',
    showComments: () => true,
    extraCommentBlocks: () => [],
    validationError: () => '',
    refetch: fetchResults,
  };
  const apply = setupApply(parts, brand, adapter);

  // "Talk to an adviser": the application dialog with no product chosen.
  parts.all<HTMLButtonElement>('[data-talk-adviser]').forEach((btn) => {
    btn.addEventListener('click', () => { state.selectedCode = null; apply.openApplyDialog(); });
  });

  render();
  updateSections();
  void fetchResults();
}
